<template>
  <div
    v-if="isOpen"
    class="image-viewer-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="image-viewer-title"
  >
    <header class="image-viewer-header">
      <div class="image-viewer-heading">
        <i class="fa fa-picture-o" aria-hidden="true"></i>
        <h5 id="image-viewer-title">圖片檢視</h5>
        <span class="image-viewer-divider" aria-hidden="true"></span>
        <span class="image-viewer-count">{{ currentIndex + 1 }} / {{ images.length }}</span>
      </div>
      <button
        ref="closeButton"
        type="button"
        class="image-viewer-close"
        title="關閉"
        aria-label="關閉圖片檢視器"
        @click="close"
      >
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
    </header>

    <main class="image-viewer-content">
      <button
        type="button"
        class="image-viewer-navigation image-viewer-previous"
        :disabled="images.length <= 1"
        title="上一張"
        aria-label="上一張圖片"
        @click="changeImage(-1)"
      >
        <i class="fa fa-angle-left" aria-hidden="true"></i>
      </button>

      <div ref="canvas" class="image-viewer-canvas" @wheel.prevent="handleWheel">
        <p v-if="hasError" class="image-viewer-error" role="alert">
          圖片載入失敗，請稍後再試。
        </p>
        <div v-else ref="panzoomTarget" class="image-viewer-stage">
          <img
            ref="image"
            :src="src"
            :alt="alt"
            :style="imageStyle"
            draggable="false"
            @load="initializePanzoom"
            @error="handleImageError"
          >
        </div>
      </div>

      <button
        type="button"
        class="image-viewer-navigation image-viewer-next"
        :disabled="images.length <= 1"
        title="下一張"
        aria-label="下一張圖片"
        @click="changeImage(1)"
      >
        <i class="fa fa-angle-right" aria-hidden="true"></i>
      </button>

      <div class="image-viewer-toolbar" role="toolbar" aria-label="圖片操作">
        <button type="button" :disabled="!panzoom" title="縮小" aria-label="縮小" @click="zoomOut">
          <i class="fa fa-search-minus" aria-hidden="true"></i>
        </button>
        <span class="image-viewer-tool-divider" aria-hidden="true"></span>
        <button type="button" :disabled="!panzoom" title="放大" aria-label="放大" @click="zoomIn">
          <i class="fa fa-search-plus" aria-hidden="true"></i>
        </button>
        <span class="image-viewer-tool-divider" aria-hidden="true"></span>
        <output class="image-viewer-scale" aria-live="polite">{{ zoomPercent }}%</output>
        <span class="image-viewer-tool-divider" aria-hidden="true"></span>
        <button type="button" :disabled="!panzoom" title="向左旋轉" aria-label="向左旋轉" @click="rotate(-90)">
          <i class="fa fa-undo" aria-hidden="true"></i>
        </button>
        <span class="image-viewer-tool-divider" aria-hidden="true"></span>
        <button type="button" :disabled="!panzoom" title="向右旋轉" aria-label="向右旋轉" @click="rotate(90)">
          <i class="fa fa-repeat" aria-hidden="true"></i>
        </button>
        <span class="image-viewer-tool-divider" aria-hidden="true"></span>
        <button type="button" :disabled="!panzoom" title="重設" aria-label="重設圖片" @click="reset">
          <i class="fa fa-refresh" aria-hidden="true"></i>
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import Panzoom from "@panzoom/panzoom";

export default {
  name: "ImageViewerModal",
  data() {
    return {
      isOpen: false,
      images: [],
      currentIndex: 0,
      src: "",
      alt: "",
      hasError: false,
      panzoom: null,
      rotation: 0,
      zoomPercent: 100,
      previousBodyOverflow: "",
      bodyAlreadyHadModalClass: false,
      previouslyFocusedElement: null,
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `rotate(${this.rotation}deg)`,
      };
    },
  },
  beforeDestroy() {
    this.releaseModalState();
  },
  methods: {
    open({ src = "", alt = "", images = null, index = 0 }) {
      const availableImages = Array.isArray(images)
        ? images.filter((image) => image && image.src)
        : [];

      this.images = availableImages.length ? availableImages : [{ src, alt }];
      if (!this.images[0] || !this.images[0].src) return;

      this.currentIndex = Math.min(Math.max(index, 0), this.images.length - 1);
      this.setCurrentImage();
      this.previouslyFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      this.bodyAlreadyHadModalClass = document.body.classList.contains("modal-open");
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
      document.addEventListener("keydown", this.handleKeydown);
      this.isOpen = true;

      this.$nextTick(() => {
        if (this.$refs.closeButton) this.$refs.closeButton.focus();
        this.initializeLoadedImage();
      });
    },
    close() {
      if (!this.isOpen) return;

      this.releaseModalState();
      this.isOpen = false;
      this.src = "";
      this.images = [];

      this.$nextTick(() => {
        if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
          this.previouslyFocusedElement.focus();
        }
        this.previouslyFocusedElement = null;
      });
    },
    setCurrentImage() {
      const image = this.images[this.currentIndex];
      this.destroyPanzoom();
      this.src = image.src;
      this.alt = image.alt || "";
      this.hasError = false;
      this.rotation = 0;
      this.zoomPercent = 100;
    },
    changeImage(offset) {
      if (this.images.length <= 1) return;

      this.currentIndex = (this.currentIndex + offset + this.images.length) % this.images.length;
      this.setCurrentImage();
      this.$nextTick(this.initializeLoadedImage);
    },
    initializeLoadedImage() {
      if (this.$refs.image && this.$refs.image.complete && this.$refs.image.naturalWidth) {
        this.initializePanzoom();
      }
    },
    initializePanzoom() {
      if (!this.isOpen || !this.$refs.panzoomTarget || this.panzoom) return;

      this.panzoom = Panzoom(this.$refs.panzoomTarget, {
        minScale: 1,
        maxScale: 8,
        step: 0.25,
        origin: "50% 50%",
        panOnlyWhenZoomed: true,
        animate: true,
      });
      this.$refs.panzoomTarget.addEventListener("panzoomchange", this.handlePanzoomChange);
      this.panzoom.reset({ animate: false, force: true });
    },
    destroyPanzoom() {
      if (this.$refs.panzoomTarget) {
        this.$refs.panzoomTarget.removeEventListener("panzoomchange", this.handlePanzoomChange);
      }
      if (this.panzoom) {
        this.panzoom.destroy();
        this.panzoom = null;
      }
    },
    releaseModalState() {
      this.destroyPanzoom();
      document.removeEventListener("keydown", this.handleKeydown);
      document.body.style.overflow = this.previousBodyOverflow;
      if (!this.bodyAlreadyHadModalClass) document.body.classList.remove("modal-open");
    },
    handlePanzoomChange(event) {
      if (event.detail && event.detail.scale) {
        this.zoomPercent = Math.round(event.detail.scale * 100);
      }
    },
    handleWheel(event) {
      if (!this.panzoom) return;
      if (event.deltaY < 0) this.panzoom.zoomIn({ animate: false });
      else this.panzoom.zoomOut({ animate: false });
    },
    zoomIn() {
      if (this.panzoom) this.panzoom.zoomIn();
    },
    zoomOut() {
      if (this.panzoom) this.panzoom.zoomOut();
    },
    rotate(degrees) {
      if (!this.panzoom) return;
      this.rotation = (this.rotation + degrees + 360) % 360;
    },
    reset() {
      if (!this.panzoom) return;
      this.rotation = 0;
      this.zoomPercent = 100;
      this.panzoom.reset({ animate: true });
    },
    handleImageError() {
      this.destroyPanzoom();
      this.hasError = true;
    },
    handleKeydown(event) {
      if (event.key === "Escape") this.close();
      else if (event.key === "ArrowLeft") this.changeImage(-1);
      else if (event.key === "ArrowRight") this.changeImage(1);
    },
  },
};
</script>

<style scoped>
.image-viewer-modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #fff;
  background: #1b1d21;
}

.image-viewer-header {
  position: relative;
  z-index: 3;
  display: flex;
  min-height: 88px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  background: #17191d;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.03);
}

.image-viewer-heading {
  display: flex;
  align-items: center;
  gap: 16px;
}

.image-viewer-heading > .fa {
  font-size: 30px;
}

.image-viewer-heading h5 {
  margin: 0;
  color: #fff;
  font-size: 24px;
  font-weight: 500;
}

.image-viewer-divider {
  width: 1px;
  height: 28px;
  margin: 0 8px;
  background: #56585d;
}

.image-viewer-count {
  min-width: 64px;
  font-size: 23px;
  font-weight: 500;
  text-align: center;
}

.image-viewer-close,
.image-viewer-navigation,
.image-viewer-toolbar button {
  border: 0;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.image-viewer-close {
  width: 48px;
  height: 48px;
  padding: 0;
  font-size: 34px;
}

.image-viewer-close:hover,
.image-viewer-close:focus,
.image-viewer-navigation:hover:not(:disabled),
.image-viewer-navigation:focus:not(:disabled),
.image-viewer-toolbar button:hover:not(:disabled),
.image-viewer-toolbar button:focus:not(:disabled) {
  color: #ff9a56;
  outline: none;
}

.image-viewer-content {
  position: relative;
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  align-items: stretch;
  overflow: hidden;
  background: radial-gradient(circle at center, #24272c 0%, #1b1d21 72%);
}

.image-viewer-canvas {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  padding: 32px 110px 132px;
  overflow: hidden;
  cursor: grab;
  touch-action: none;
}

.image-viewer-canvas:active {
  cursor: grabbing;
}

.image-viewer-stage {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
}

.image-viewer-stage img {
  display: block;
  max-width: calc(100vw - 280px);
  max-height: calc(100vh - 290px);
  object-fit: contain;
  transform-origin: center center;
  transition: transform 0.2s ease;
  user-select: none;
  pointer-events: none;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.32);
}

.image-viewer-error {
  padding: 16px 24px;
  border-radius: 6px;
  color: #721c24;
  background: #f8d7da;
}

.image-viewer-navigation {
  position: absolute;
  z-index: 2;
  top: 50%;
  width: 72px;
  height: 96px;
  padding: 0;
  font-size: 56px;
  transform: translateY(-60%);
}

.image-viewer-previous {
  left: 30px;
}

.image-viewer-next {
  right: 30px;
}

.image-viewer-navigation:disabled {
  cursor: default;
  opacity: 0.22;
}

.image-viewer-toolbar {
  position: absolute;
  z-index: 3;
  bottom: 48px;
  left: 50%;
  display: flex;
  min-height: 76px;
  align-items: center;
  justify-content: center;
  padding: 0 28px;
  border: 1px solid #414348;
  border-radius: 42px;
  background: #2a2c30;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  transform: translateX(-50%);
}

.image-viewer-toolbar button {
  width: 62px;
  height: 58px;
  padding: 0;
  font-size: 29px;
}

.image-viewer-toolbar button:disabled {
  cursor: wait;
  opacity: 0.35;
}

.image-viewer-tool-divider {
  width: 1px;
  height: 38px;
  background: #55575c;
}

.image-viewer-scale {
  min-width: 104px;
  color: #fff;
  font-size: 22px;
  text-align: center;
}

@media (max-width: 767.98px) {
  .image-viewer-header {
    min-height: 64px;
    padding: 0 16px;
  }

  .image-viewer-heading {
    gap: 9px;
  }

  .image-viewer-heading > .fa,
  .image-viewer-heading h5,
  .image-viewer-count {
    font-size: 18px;
  }

  .image-viewer-divider {
    height: 22px;
    margin: 0 3px;
  }

  .image-viewer-close {
    width: 40px;
    height: 40px;
    font-size: 27px;
  }

  .image-viewer-canvas {
    padding: 20px 44px 106px;
  }

  .image-viewer-stage img {
    max-width: calc(100vw - 100px);
    max-height: calc(100vh - 220px);
  }

  .image-viewer-navigation {
    width: 42px;
    font-size: 42px;
  }

  .image-viewer-previous {
    left: 2px;
  }

  .image-viewer-next {
    right: 2px;
  }

  .image-viewer-toolbar {
    bottom: 22px;
    min-height: 60px;
    padding: 0 10px;
  }

  .image-viewer-toolbar button {
    width: 43px;
    height: 46px;
    font-size: 22px;
  }

  .image-viewer-tool-divider {
    height: 28px;
  }

  .image-viewer-scale {
    min-width: 68px;
    font-size: 17px;
  }
}
</style>
